import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, MapPin, DollarSign, Bed, Bath } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  bedrooms: number;
  bathrooms: number;
  rent_amount: number;
  status: string;
  description: string | null;
}

interface PropertyComparisonProps {
  properties: Property[];
  open: boolean;
  onClose: () => void;
  onRemove: (id: string) => void;
}

export const PropertyComparison = ({ properties, open, onClose, onRemove }: PropertyComparisonProps) => {
  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      available: "default",
      occupied: "secondary",
      maintenance: "outline"
    };
    return variants[status] || "outline";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Property Comparison</DialogTitle>
          <DialogDescription>
            Compare {properties.length} properties side-by-side
          </DialogDescription>
        </DialogHeader>

        {properties.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Select properties to compare</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {properties.map((property) => (
              <Card key={property.id} className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 z-10"
                  onClick={() => onRemove(property.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
                <CardHeader>
                  <div className="flex items-start justify-between pr-8">
                    <CardTitle className="text-lg">{property.name}</CardTitle>
                  </div>
                  <Badge variant={getStatusBadge(property.status)} className="w-fit">
                    {property.status}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      <div>
                        <p>{property.address}</p>
                        <p>{property.city}, {property.state} {property.zip}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold text-primary">
                        ${property.rent_amount.toLocaleString()}/mo
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Bed className="h-4 w-4 text-muted-foreground" />
                        <span>{property.bedrooms} bed</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bath className="h-4 w-4 text-muted-foreground" />
                        <span>{property.bathrooms} bath</span>
                      </div>
                    </div>
                  </div>

                  {property.description && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {property.description}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
